import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import style from './index.scss';

import moment from 'moment';
import { toLower } from 'lodash';

import Dropdown from '../../../../components/Dropdown';
import NestPieChart from '../../../../components/ns/NestPieChart';
import FilterDropdown from '../../../../components/FilterDropdown';
import FigureHeatmap from '../../../../components/FigureHeatmap';
import NoDataIsLoadingWrapper from '../../../../components/NoDataIsLoadingWrapper';

import {
  nsColorConstants,
  dateTimeFormat
} from '../../../../util/GlobalConstant';

import BusinessSegmentViewSwitcher from '../switchers/viewSwitcher';

import { filterGroupData, heatMapColumns } from '../metadata';

import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Bar,
  CartesianGrid,
  Tooltip,
  Line,
  Legend
} from 'recharts';

export const HEATMAP_VIEW = 0;
export const GRAPHICAL_VIEW = 1;

const ChartView = ({
  selectedSegment,
  businessParamsFilter,
  trendData,
  data,
  filterParameters,
  filterGroupData,
  onChartBusinessParametersFilterChange
}) => {
  return (
    <div>
      <div className="row">
        <div className="offset-md-6 col-md-6">
          <div className={style.paramIndicatorList}>
            <ul>
              <li>
                Alerts <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.alerts }}></i>
              </li>
              <li>
                Hits <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.hits }}></i>
              </li>
              <li>
                True hits <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.trueHits }}></i>
              </li>
              <li>
                False hits <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.falseHits }}></i>
              </li>
              <li>
                Undetermined Hits <br />
                <i
                  className={style.indicator}
                  style={{
                    backgroundColor: nsColorConstants.undetermined
                  }}></i>
              </li>
              <li>
                L1 Hits <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.autoClosed }}></i>
              </li>
              <li>
                Hits Yield <br />
                <i
                  className={style.indicator}
                  style={{ backgroundColor: nsColorConstants.hitsYield }}></i>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-10">
          <h5>{selectedSegment.name}</h5>
        </div>

        <div className="col-md-2">
          <FilterDropdown
            filterGroupData={filterGroupData}
            handleConfirm={onChartBusinessParametersFilterChange}
            userFilterSetting={businessParamsFilter}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart
              data={trendData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={dateTickFormatter} />
              <YAxis
                type="number"
                domain={['dataMin', (dataMax) => Math.ceil(dataMax * 1.05)]}
              />
              <Tooltip content={<GroupBarChartToolTip />} />
              {filterParameters.totalAlerts ? (
                <Bar
                  dataKey="totalAlerts"
                  stackId="primary"
                  fill={nsColorConstants.alerts}
                />
              ) : null}
              {filterParameters.undetermined ? (
                <Bar
                  dataKey="undetermined"
                  stackId="secondary"
                  fill={nsColorConstants.undetermined}
                />
              ) : null}
              {filterParameters.level1 ? (
                <Bar
                  dataKey="level1"
                  stackId="secondary"
                  fill={nsColorConstants.autoClosed}
                />
              ) : null}
              {filterParameters.falseHits ? (
                <Bar
                  dataKey="falseHits"
                  stackId="secondary"
                  fill={nsColorConstants.falseHits}
                />
              ) : null}
              {filterParameters.trueHits ? (
                <Bar
                  dataKey="trueHits"
                  stackId="secondary"
                  fill={nsColorConstants.trueHits}
                />
              ) : null}
              {filterParameters.totalAlerts ? (
                <Line
                  type="monotone"
                  dataKey="totalAlerts"
                  stroke={nsColorConstants.alerts}
                  fill={nsColorConstants.alerts}
                />
              ) : null}
              {filterParameters.totalHits ? (
                <Line
                  type="monotone"
                  dataKey="totalHits"
                  stroke={nsColorConstants.hits}
                  fill={nsColorConstants.hits}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={classnames('row')}>
        <div
          className={classnames('col')}
          style={{ borderRight: '1px solid #ccc' }}>
          <NestPieChart data={data.all} name="All" />
        </div>
        <div className={classnames('col')}>
          <NestPieChart data={data.sanctions} name="Sanctions" />
        </div>
        <div className={classnames('col')}>
          <NestPieChart data={data.adverseMedia} name="Adverse Media" />
        </div>
        <div className={classnames('col')}>
          <NestPieChart data={data.pep} name="PEP" />
        </div>
        <div className={classnames('col')}>
          <NestPieChart data={data.others} name="Others" />
        </div>
      </div>
    </div>
  );
};

const HeatMapView = ({ data, selectedSegment }) => {
  const allData = data.all ? data.all : null;
  if (allData) {
    const formattedHeatmapData = {
      data: [
        {
          id: selectedSegment.id,
          name: selectedSegment.name,
          data: {
            all: heatMapColumns.map((column) => {
              if (column.id === 'totalAlerts') {
                return {
                  id: column.id,
                  value: allData[column.id]
                };
              }

              if (column.id === 'totalHits') {
                return {
                  id: column.id,
                  value: allData[column.id]
                };
              }

              return {
                id: column.id,
                value: allData[column.id].count
              };
            })
          }
        }
      ]
    };
    return (
      <div>
        <div className="row">
          <div className="col-md-12">
            <FigureHeatmap
              name="segmentsByHeatmap"
              columns={heatMapColumns}
              data={formattedHeatmapData}
              fillColor="#63adc3"
              tableFilter={{}}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export const BusinessSegmentsAndParameters = (props) => {
  const {
    data,
    selectedSegment,
    segmentList,
    onSegmentChange,
    activeGraphView,
    onGraphViewChange,
    isLoading,
    onReload,
    onChartBusinessParametersFilterChange,
    businessParamsFilter
  } = props;

  if (!isLoading && !data) {
    return <div />;
  }

  if (isLoading) {
    return (
      <NoDataIsLoadingWrapper
        title="Business Segments & Parameters"
        isLoadingDataText="Loading Business Segments and Parameters chart data..."
        isCollapsed={false}>
        <BusinessSegmentViewSwitcher
          onViewSwitchChange={onGraphViewChange}
          activeView={activeGraphView}
        />
      </NoDataIsLoadingWrapper>
    );
  }

  if (data) {
    const trendData = data?.alertAndHit ? data.alertAndHit : [];
    const allSegmentOption = {
      code: '-',
      id: '-',
      name: 'All Segments'
    };
    const segmentOptions = [allSegmentOption];
    segmentList.map((segment) => {
      segmentOptions.push({
        code: segment.code,
        id: segment.code,
        name: segment.name
      });
    });

    const filterParameters = businessParamsFilter.Parameters;

    let currentView = null;

    if (activeGraphView === HEATMAP_VIEW) {
      currentView = (
        <HeatMapView data={data} selectedSegment={selectedSegment} />
      );
    }

    if (activeGraphView === GRAPHICAL_VIEW) {
      currentView = (
        <ChartView
          selectedSegment={selectedSegment}
          businessParamsFilter={businessParamsFilter}
          trendData={trendData}
          data={data}
          filterGroupData={filterGroupData}
          onChartBusinessParametersFilterChange={
            onChartBusinessParametersFilterChange
          }
          filterParameters={filterParameters}
        />
      );
    }

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>Business Segments & Parameters</p>
            </div>

            <div className="col-md-4">
              <p className="text-right">
                <Dropdown
                  dropdownValue={selectedSegment}
                  dropdownOptions={segmentOptions}
                  handleValueChange={onSegmentChange}
                />
              </p>
            </div>

            <div className="col-md-2">
              <p className="text-right">
                <BusinessSegmentViewSwitcher
                  onViewSwitchChange={onGraphViewChange}
                  activeView={activeGraphView}
                />
              </p>
            </div>
          </div>
        </div>

        <div className="card-body">{currentView}</div>
      </div>
    );
  }

  return (
    <NoDataIsLoadingWrapper
      onReload={onReload}
      title="Business Segments & Parameters"
      noDataText="No Business Segments and Parameters data exists for the period selected."
      reloadDataText="Click here to fetch alerts and hits data again."
      isCollapsed={false}>
      <BusinessSegmentViewSwitcher
        onViewSwitchChange={onGraphViewChange}
        activeView={activeGraphView}
      />
    </NoDataIsLoadingWrapper>
  );
};

const dateTickFormatter = (tickitem) => {
  const tickDate = moment(tickitem, [dateTimeFormat]);
  if (tickDate.isValid()) {
    return moment.utc(tickitem, [dateTimeFormat]).local().format('DD/MM/YYYY');
  }

  return '';
};

const GroupBarChartToolTip = (props) => {
  const { active, payload, label } = props;

  let liElements = [];
  payload &&
    payload.forEach((item, i) => {
      if (item.stroke && item.dataKey == 'alerts') {
        //skip alerts line
      } else {
        liElements.push(
          <li key={`bar-item-${i}`} style={{ color: `${item.fill}` }}>
            {getNSReadableName(item.name)}:{' '}
            <span style={{ float: 'right' }}>{item.value}</span>
          </li>
        );
      }
    });

  const labelValue = moment.utc(label, [dateTimeFormat]);

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            {labelValue.isValid()
              ? labelValue.local().format('DD/MM/YYYY')
              : label}
          </p>
          <ul className="list-unstyled">{liElements}</ul>
        </div>
      </div>
    </div>
  ) : null;
};

const getNSReadableName = (name) => {
  switch (toLower(name)) {
    case 'totalalerts':
      return 'Alerts';
    case 'totalhits':
      return 'Hits';
    case 'hityield':
      return 'Hits Yield';
    case 'level_1':
    case 'level1':
      return 'L1 Hits';
    case 'undetermined':
      return 'Undetermined Hits';
    case 'truehits':
      return 'True Hits';
    case 'falsehits':
      return 'False Hits';
    default:
      return name;
  }
};

BusinessSegmentsAndParameters.propTypes = {
  data: PropTypes.object,
  segmentList: PropTypes.array,
  selectedSegment: PropTypes.object,
  businessParamsFilter: PropTypes.object,
  onSegmentChange: PropTypes.func,
  isLoading: PropTypes.bool,
  activeGraphView: PropTypes.number,
  onGraphViewChange: PropTypes.func,
  onReload: PropTypes.func,
  onChartBusinessParametersFilterChange: PropTypes.func
};
